import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory, createMapSelector, compareArray } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import moment from "moment-timezone";
import deepmerge from "deepmerge";
import { useSelector } from "react-redux";
import { maxDate, minDate, nowDate, parseDate } from "../../common/utils/dateUtils";
import { ensureArray, intersect } from "../../common/utils/arrayUtilities";
import { useMemo } from "react";
import { deepMerge } from "../../common/utils/objectUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { getDisplayActualTimes, prepareRosterArray } from "./rosterSliceUtils";
import { dynamicSort } from "../../common/utils/tableUtilities";
import { config } from "process";

//TODO: Cleanup/move non-mgr specific things

const key = roster => ensureInteger(roster.rosterid);

const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'roster_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'roster_one');

const createRosterOnServer = (network, roster) => network.post('/api/v2/roster', roster, visualizeSuccess('create', rosterMgrSlice.actions.addOrUpdate), visualizeFailure('create'));
const updateRosterOnServer = (network, delta) => network.put('/api/v2/roster', delta, visualizeSuccess('update', rosterMgrSlice.actions.addOrUpdate), visualizeFailure('update'));
export const removeRosterOnServer = (network, rosterid, reason = {}) => network.post('/api/v2/deleteRoster/' + encodeURIComponent(rosterid), reason, visualizeSuccess('remove', rosterMgrSlice.actions.remove), visualizeFailure('remove'));
export const rejectRosterOnServer = (network, rosterid) => network.put('/api/v1/roster', {rosterid, removal_request: false}, visualizeSuccess('update', rosterMgrSlice.actions.addOrUpdate), visualizeFailure('get'));
export const confirmRosterOnServer = (network, data) => network.put('/api/v1/rosterConfirmation', data, visualizeSuccess('update'), visualizeFailure('update'));

export const publishRoster = createAsyncThunk(
    'rosters/publishRoster',
    async (network, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
        const state = getState();   
        const wip = state.rosters.wip;
        
        const results = await Promise.allSettled(Object.entries(wip).map(async ([internalid, delta]) => {
            switch (delta.kind) {
                case 'upsert': {
                    const toSend = { ...delta.data };
                        delete toSend.display_actual_start;
                        delete toSend.display_actual_end;
                        delete toSend.shift_start;
                        delete toSend.shift_end;
                    if (delta.data.rosterid) {
                        delete toSend.internalid;
                        const hasUpdatedFields = Object.keys(toSend).some(key => key !== 'rosterid');                       
                        if (hasUpdatedFields) {
                            await updateRosterOnServer(network, toSend);
                        }
                    } else {
                        await createRosterOnServer(network, toSend);
                    }
                    break;
                }
                case 'remove': {
                    await removeRosterOnServer(network, delta.data.rosterid, delta.data.reason);
                    break;
                }
                default:
                    throw new Error('Unknown WIP kind ' + delta.kind);
            }
            return internalid;
        }));
        if (results.every(r => r.status === 'fulfilled')) {
            dispatch(rosterMgrSlice.actions.clearWIP());
            fulfillWithValue(true);
        } else {
            rejectWithValue({ processed: results.filter(r => r.status === 'fulfilled').map(r => r.value), errors: results.filter(r => r.status === 'rejected').map(r => r.reason)} )
        }
    }
);

const updateState = (state, roster) => {
    state.data[key(roster)] = roster;
}
const removeState = (state, rosterid) => {
    delete state.data[rosterid];
}

export const rosterMgrSlice = createSlice({
    name: 'rosters',
    initialState: {
        data: {},
        roles: [],
        wip: {}
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toProcess.delete(key(roster));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        setForGroups: (state, action) => {
            const groupids = new Set(action.payload.groupids);
            const toRemove = new Set(Object.values(state.data).filter(r => groupids.has(r.groupid)).map(r => key(r)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        setForShifts: (state, action) => {
            const shiftids = new Set(action.payload.shiftids);
            const toRemove = new Set(Object.values(state.data).filter(r => shiftids.has(r.shiftid)).map(r => key(r)));
            prepareRosterArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        addOrUpdate: (state, action) => {
            prepareRosterArray(action.payload).forEach(r => state.data[key(r)] = r);
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    const { display_actual_start, display_actual_end } = getDisplayActualTimes(roster);
                    if (roster.shiftid === shift.id) {
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                        roster.travel = shift.travel;
                        roster.display_actual_start = display_actual_start;
                        roster.display_actual_end = display_actual_end;
                    }
                }
                // New rosters have shift_start, shift_end and shift_travel set since the derrived fields are not back from the server
                // update shift_start, shift_end and shift_travel where necessary
                for (const wipRoster of Object.values(state.wip)) {
                    if (wipRoster.shiftid === shift.id) {
                        const { display_actual_start, display_actual_end } = getDisplayActualTimes(wipRoster);
                        if ('shift_start' in wipRoster) {
                            wipRoster.shift_start = shift.start;
                        }
                        if ('shift_end' in wipRoster) {
                            wipRoster.shift_end = shift.end;
                        }
                        if ('shift_travel' in wipRoster) {
                            wipRoster.shift_travel = shift.travel;
                        }
                        wipRoster.display_actual_start = display_actual_start;
                        wipRoster.display_actual_end = display_actual_end;
                    }
                }
            })

        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => delete state.data[key(roster)])
        },
        setRoles: (state, action) => {
            state.roles = action.payload;
        },
        addOrUpdateWIP: (state, action) => {
            const internalid = action.payload.internalid
            if (!state.wip[internalid]) {
                state.wip[internalid] = { kind: 'upsert', data: action.payload }
            } else {
                state.wip[internalid] = { kind: 'upsert', data: deepMerge(state.wip[internalid].data, action.payload) };
            }
        },
        markRemovedWIP: (state, action) => {
            if (action.payload.rosterid) {
                state.wip[action.payload.internalid] = { kind: 'remove', data: action.payload };
            } else {
                delete state.wip[action.payload.internalid];
            }
        },
        clearWIP: (state, action) => {
            state.wip = {};
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => rosterMgrSlice.getInitialState());
        builder.addCase(publishRoster.fulfilled, (state) => {
            state.wip = {};
        });
        builder.addCase(publishRoster.rejected, (state, action) => {
            action.payload.processed?.forEach(internalid => delete state.wip[internalid]);
        })
    }
});

// #region network operations

const fetchRosterRoles = network => network.get('/api/v1/rosterRoles', rosterMgrSlice.actions.setRoles, visualizeFailure('get'));
const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/roster/' + encodeURIComponent(id), data => rosterMgrSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/rosters', ids, data => rosterMgrSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchGroupRoster = aggregatingFetch(
    groupid => network => network.get('/api/v2/rostersForGroup/' + encodeURIComponent(groupid), data => rosterMgrSlice.actions.setForGroups({ groupids: [groupid], data }), visualizeFailure('get')),
    groupids => network => network.post('/api/v2/rostersForGroups', groupids, data => rosterMgrSlice.actions.setForGroups({ groupids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchShiftRosters = aggregatingFetch(
    shiftid => network => network.get('/api/v2/rostersForShift/' + encodeURIComponent(shiftid), data => rosterMgrSlice.actions.setForShifts({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/rostersForShifts', shiftids, data => rosterMgrSlice.actions.setForShifts({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchMemberRostersInRange = (memberid, start, end) => network => network.get(`/api/v2/rosters/member/${encodeURIComponent(memberid)}/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'));

const fetchRostersInRange = aggregatingFetch(
    (start, end, config, all) => network => {
        return network.get(`/api/v2/rosters/${encodeURIComponent(start)}/${encodeURIComponent(end)}${all ? '?all=1' : ''}`, rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
    },
    keys => network => {
        return network.get(`/api/v2/rosters/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}${keys.some(key => key[3]) ? '?all=1' : ''}`, rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
    }
)

const fetchMemberLastRoster = aggregatingFetch(
    (memberid, clientConfig) => network => network.post('/api/v2/memberLastRoster', [memberid], rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    args => network => network.post('/api/v2/memberLastRoster', args.map(arg => arg[0]), rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
)
export const fetchRosterRemovalRequests = () => (network) => network.post('/api/v1/removalRequests', {}, rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'));
const fetchUnconfirmedRostersInRange = (start, end, config) => network => network.post('/api/v2/rosterUnconfirmed', {start, end}, rosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
export const rosterDefaults = { travel: 0, hours_confirmed: false, removal_request: false }; 
export const createRoster = (dispatch, network, roster, shift) => dispatch(rosterMgrSlice.actions.addOrUpdateWIP({ ...rosterDefaults, shift_start: shift.start, shift_end: shift.end, internalid: crypto.randomUUID(), ...roster }));
export const updateRoster = (dispatch, network, roster, shift) => dispatch(rosterMgrSlice.actions.addOrUpdateWIP(roster));
// make sure you use the RosterRemoveButtonWrapper to call this with the reason
export const removeRoster = (dispatch, network, roster, reason) => dispatch(rosterMgrSlice.actions.markRemovedWIP({ ...roster, reason }));

export const clearWIPRoster = (dispatch, network) => dispatch(rosterMgrSlice.actions.clearWIP({}));
// #endregion

// #region selectors
const selectRosterRoles = state => state.rosters.roles;
const selectRawRosters = state => state.rosters.data;
const selectDataByShift = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.shiftid]) {
                res[r.shiftid] = [];
            }
            res[r.shiftid].push(r);
            return res;
        }, {});
    }
);
const selectDataByGroup = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.groupid]) {
                res[r.groupid] = [];
            }
            res[r.groupid].push(r);
            return res;
        }, {});
    }
);
const selectRawWIP = state => state.rosters.wip;
const mergeWip = (rosters, wip, filter = _ => true) => {
    const wipKeys = Object.keys(wip);
    if (wipKeys.length === 0) {
        return rosters
    }
    wipKeys.forEach(internalid => {
        const wipEntry = wip[internalid];
        const idx = rosters.findIndex(r => r.internalid === internalid);
        switch (wipEntry.kind) {
            case 'remove': {
                rosters.splice(idx, 1);
                break;
            }
            case 'upsert': {
                if (idx >= 0) {
                    rosters.splice(idx, 1, deepmerge(JSON.parse(JSON.stringify(rosters[idx])), wipEntry.data));
                } else {
                    rosters.push(wipEntry.data);
                }
                break;
            }
            default:
                throw new Error('Unknown wipEntry kind ' + wipEntry.kind);
        }
    });
    return rosters;
}

const selectMergedRosters = createSelector([selectRawRosters, selectRawWIP], (rosters, wip) => {
    return mergeWip(Object.values(rosters), wip);
});

const selectRoster = field => value => {
    return createSelector([selectMergedRosters], (rosters) => {
        return rosters?.find(r => r[field] === value);
    });
}
const selectRosterByRosterid = selectRoster('rosterid');
const selectRosterByInternalid = selectRoster('internalid');
const selectRosters = field => values => {
    return createSelector([selectMergedRosters], rosters => {
        return ensureArray(values).reduce((res, value) => {
            const r = rosters?.find(r => r[field] === value);
            if (r) {
                res.push(r);
            }
            return res;
        }, []);
    })
}
const selectRostersByInternalid = selectRosters('internalid');

export const selectShiftRosters = (shiftidsInput) => {
    return createSelector(
        [selectDataByShift, state => state.rosters.wip, () => shiftidsInput],
        (shiftRosters, wip, shiftidsInput) => {
            const shiftids = ensureArray(shiftidsInput);
            const rosters = shiftids.flatMap(shiftid => shiftRosters[shiftid] ?? [])
            return mergeWip(
                rosters,
                wip,
                wipEntry => wipEntry.data.shiftid && shiftids.includes(wipEntry.data.shiftid)
            );
        }
    );
};

export const selectGroupRosters = (groupidsInput) => {
    const groupids = ensureArray(groupidsInput); // normalize once for consistency

    return createSelector(
        [selectDataByGroup, state => state.rosters.wip],
        (groupRosters, wip) => {
            const rosters = groupids.flatMap(groupid => groupRosters[groupid] ?? [])
            return mergeWip(
                rosters,
                wip,
                wipEntry => wipEntry.data.shiftid && groupids.includes(wipEntry.data.shiftid)
            );
        }
    );
};

const selectRosterRemovalsRequests = () => createSelector([selectRawRosters], rosters => Object.values(rosters)?.filter(r => r.removal_request !== false) );
const selectMemberRostersInRange = (memberid, start, end) => createSelector([selectMergedRosters, () => memberid, () => start, () => end], (rosters, memberid, start, end) => {
    const s = new Date(start).getTime(), e = new Date(end).getTime();
    return rosters?.filter(r => r.memberid === memberid && s < new Date(r.end ?? r.shift_end) && new Date(r.start ?? r.shift_start) < e) ?? []
}, );
const selectRostersInRange = (start, end, config, all) => createSelector([selectMergedRosters, () => start, () => end, () => config, () => all], (rosters, start, end, config, all) => {
    const s = new Date(start).getTime(), e = new Date(end).getTime();
    return rosters?.filter(r => {
        return s < new Date(r.end ?? r.shift_end) && new Date(r.start ?? r.shift_start) < e && (all || isActive(r, config))
    }) ?? []
});
const selectUnconfirmedRosters = (start, end, config) => createSelector([selectMergedRosters, () => start, () => end, () => config], (rosters, start, end, config) => {
    const s = new Date(start).getTime(), e = new Date(end).getTime();
    return rosters?.filter(r => !r.hours_confirmed && isActive(r, config) && s < new Date(r.end ?? r.shift_end) && new Date(r.start ?? r.shift_start) < e && parseDate(config.rosterConfirmationDuringShift ? (r.start ?? r.shift_start) : (r.end ?? r.shift_end)) < new Date()) ?? []
});
const selectLastRoster = (memberid, clientConfig) => createSelector([selectMergedRosters, () => memberid, () => clientConfig], (rosters, memberid, clientConfig) => {
    const memberRosters = rosters?.filter(r => r.memberid === memberid && isActive(r, clientConfig) && parseDate(r.display_actual_start) < Date.now());
    memberRosters.sort(dynamicSort(['-display_actual_start']));
    return memberRosters.length > 0 ? memberRosters[0] : null;
})
// #endregion

const listen = {
    rosterAdminCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'roster', key(roster));
        dispatch(rosterMgrSlice.actions.addOrUpdate(roster))
    },
    rosterAdminUpdated: (roster, { dispatch }) => dispatch(rosterMgrSlice.actions.addOrUpdate(roster)),
    rosterAdminRemoved: (roster, { dispatch }) => dispatch(rosterMgrSlice.actions.remove(roster)),
    shiftAdminUpdated: (shift, { dispatch }) => dispatch(rosterMgrSlice.actions.updateShift(shift))
}

// #region utilities
export const confirmationStatus = (r, member, shift, { memberRequiresRosterConfirmation, rosterConfirmationDeadline }) => {
    if (r.hours_confirmed) {
        return 'Complete';
    } else if (r.rejected) {
        return 'Rejected'
    } else if (r.accepted) {
        return 'Confirmed'
    } else if (rosterConfirmationDeadline > 0 && r.offered && parseDate(r.offered).getTime() + (rosterConfirmationDeadline * 60 * 60 * 1000) <= nowDate().getTime()) {
        return 'Rejected';
    } else  if (!r.offered || !memberRequiresRosterConfirmation || typeof memberRequiresRosterConfirmation != 'function' || !memberRequiresRosterConfirmation(moment, { ...(member ? JSON.parse(JSON.stringify(member)) : { customfields: {}, customtables: {} }), shift_customfields: shift?.customfields ?? {}, ...r })) {
        return 'Confirmed'
    }
    return 'Offered';
}
export const getStatusColor = (roster, member, shift, clientConfig) => {
    switch (confirmationStatus(roster, member, shift, clientConfig)) {
        case 'Rejected':
            return '#f2dede';
        case 'Offered':
            return '#ffe5d0';
        default:
            return roster.removal_request ? '#f2dede' : '#dff0d8';
    }
}
export const isConfirmed = (r, member, shift, config) => {
    const status = confirmationStatus(r, member, shift, config);
    return status === 'Complete' || status === 'Confirmed';
}
export const isActive = (r, config) => {
    return !r.rejected && (!r.offered || r.accepted || !config.rosterConfirmationDeadline || parseDate(r.offered).getTime() + (config.rosterConfirmationDeadline * 60 * 60 * 1000) > nowDate().getTime())
}

const hourDiff = (a, b) => (parseDate(a) - parseDate(b)) / (60 * 60 * 1000);

export const computeRosterHours = (r, tz, { rosterRoundingInterval, rosterMinHours }) => {
    let end = r.actual_end ? r.actual_end : (r.end ?? r.shift_end);
    let start = r.actual_start ? r.actual_start : (r.start ?? r.shift_start);

    let rstart = parseDate(start);
    let rend = parseDate(end);
    
    let rawHours = hourDiff(rend, rstart);        
    let roundHours = Math.round(rawHours / rosterRoundingInterval) * rosterRoundingInterval;
    let rtravel = r.travel != null ? r.travel : r.shift_travel;

    if (rtravel) roundHours += parseFloat(rtravel);
    if (roundHours < rosterMinHours) roundHours = rosterMinHours;
    
    return isNaN(roundHours) ? 0 : roundHours;
}
// #endregion

// true: event with a template.id requires recurring access roles
// false: event with no template.id requires adhoc access roles
const rosterAccess = new Map([
    ['fullRoles', new Map([
        [true, ['recurring rostering manager', 'administrator']],
        [false, ['rostering manager', 'administrator']]
    ])],
    ['fullReadonlyRoles', new Map([
        [true, ['recurring rostering auditor', 'auditor']],
        [false, ['rostering auditor', 'auditor']]
    ])],
    ['readonlyRoles', new Map([
        [true, ['recurring rostering auditor', 'auditor', 'equipment auditor', 'equipment manager']],
        [false, ['rostering auditor', 'auditor', 'equipment auditor', 'equipment manager']]
    ])],
    ['memberDetailAccessRoles', new Map([
        [true, ['administrator','auditor','recurring rostering manager','recurring rostering auditor','equipment manager','equipment auditor','member manager','member auditor','training manager','training auditor','roster viewer','timesheet manager','timesheet auditor','kiosk']], 
        [false, ['administrator','auditor','rostering manager','rostering auditor','equipment manager','equipment auditor','member manager','member auditor','training manager','training auditor','roster viewer','timesheet manager','timesheet auditor','kiosk']]
    ])]
]);

const checkPermissions = (credentials, requiredRoles, event ) => {
    if (event) {
        return credentials.hasRole(requiredRoles, { office: event?.office, client: event?.client, location: event?.locationid })
    }
    return credentials.hasRole(requiredRoles);
};

export const canViewRosters = (credentials, event) => checkPermissions(credentials, rosterAccess.get('fullRoles').get(!!event?.templateid).concat(rosterAccess.get('readonlyRoles').get(!!event?.templateid)), event);
export const canViewProtectedFields = (credentials, event) => checkPermissions(credentials, rosterAccess.get('fullRoles').get(!!event?.templateid).concat(rosterAccess.get('fullReadonlyRoles').get(!!event?.templateid)), event);

export const hasFullRosterAccess = (credentials) => checkPermissions(credentials, Array.from(rosterAccess.get('fullRoles').values()).flat());
export const canConfirmRosters = (credentials) => checkPermissions(credentials, Array.from(rosterAccess.get('fullRoles').values()).flat().concat(['timesheet manager']));
export const canViewUnconfirmedRosters = (credentials) => checkPermissions(credentials, Array.from(rosterAccess.get('fullReadonlyRoles').values()).flat().concat(['timesheet auditor', 'timesheet manager']));
export const canViewMemberRosters = (credentials, member) => checkPermissions(credentials, Array.from(rosterAccess.get('fullRoles').values()).flat().concat( Array.from(rosterAccess.get('fullReadonlyRoles').values()).flat()), member ? { office: member.office, client: member.default_client, location: member.default_location } : member)


export const canViewMemberDetails = (credentials, clientConfig, roster, event) => {
    if (clientConfig?.disableContactList) {
        return roster?.memberid == credentials.memberID || (clientConfig?.disableContactList !== true && checkPermissions(credentials, intersect(rosterAccess.get('memberDetailAccessRoles').get(!!event?.templateid), clientConfig?.disableContactList), event))
    } else {
        return checkPermissions(credentials, rosterAccess.get('memberDetailAccessRoles').get(!!event?.templateid), event); 
    }
};

export const canUpdateRosters = (credentials, event) => checkPermissions(credentials, rosterAccess.get('fullRoles').get(!!event?.templateid), event);
export const canViewTemplateRosters = (credentials, event) => checkPermissions(credentials, rosterAccess.get('fullRoles').get(true), event);

export const canMgrViewRosters = credentials => {
    const fullRoles = rosterAccess.get('fullRoles').get(true).concat(rosterAccess.get('fullRoles').get(false));
    const readRoles = rosterAccess.get('readonlyRoles').get(true).concat(rosterAccess.get('readonlyRoles').get(false));
    return credentials.hasRole(fullRoles.concat(readRoles));
}

export const canRouteRosterList = ({credentials}) => canMgrViewRosters(credentials);
export const canRouteRosterGrid = ({credentials, clientConfig}) => canMgrViewRosters(credentials) && clientConfig.enableRosteringGrid;
export const canRouteRosterSearch = ({credentials}) => credentials.hasRole(rosterAccess.get('fullReadonlyRoles').get(true).concat(rosterAccess.get('fullReadonlyRoles').get(false)).concat(rosterAccess.get('fullRoles').get(true)).concat(rosterAccess.get('fullRoles').get(false)))
export const canRouteRosterRemovalSearch = ({credentials, clientConfig}) => credentials.hasRole(rosterAccess.get('fullReadonlyRoles').get(true).concat(rosterAccess.get('fullRoles').get(true))) && ((clientConfig.enableDeleteRosterReason === true) || clientConfig.enableDeleteRosterReason === 'confirmed')
export const canRouteRosterRemovalRequests = ({credentials, clientConfig}) => canMgrViewRosters(credentials) && (clientConfig.rosterAllowRemovalRequest);
export const canRouteRosterConfirmations = ({credentials, clientConfig}) => canViewUnconfirmedRosters(credentials) || canConfirmRosters(credentials) || (!clientConfig.disableMemberHoursConfirmation && credentials.hasRole('member'));

const options = {
    cleanup: { root: rosterMgrSlice.name, all: selectMergedRosters, removeAll: rosterMgrSlice.actions.remove, key },
    condition: canMgrViewRosters
}
const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'rosterByID' });
registerEntityBasedFetch('roster', rosterInitByID)

const [useMgrRosterByRosterID] = dataHookWithCredentialsFactory(selectRosterByRosterid, rosterInitByID, { ...options, defaultValue: null });
const [useMgrRostersByRosterID] = dataHookWithCredentialsFactory(selectRosterByRosterid, initMultiMapFactory(rosterInitByID, listen), { ...options, comparator: compareArray });

const rosterInitByShift = queryMapFactory(fetchShiftRosters, 'rosterid', rosterInitByID, listen);
registerEntityBasedFetch('shift', rosterInitByShift)

export const [useMgrRostersForShift, realizeMgrRostersForShift] = dataHookWithCredentialsFactory(selectShiftRosters, rosterInitByShift, { ...options, comparator: compareArray });
export const [useMgrRostersForShifts] = dataHookWithCredentialsFactory(selectShiftRosters, initMultiMapFactory(rosterInitByShift, listen), { ...options, comparator: compareArray });
export const realizeMgrRostersForShifts = async (context, ids) => (await Promise.all(ids.map(id => realizeMgrRostersForShift(context, id)))).flat();

const rosterInitByGroup = queryMapFactory(fetchGroupRoster, 'rosterid', rosterInitByID, listen, { name: 'rostersForGroup' });
registerEntityBasedFetch('group', rosterInitByGroup)

export const [useMgrRostersForGroup, realizeMgrRostersForGroup] = dataHookWithCredentialsFactory(selectGroupRosters, rosterInitByGroup, { ...options, comparator: compareArray });
export const [useMgrRostersForGroups] = dataHookWithCredentialsFactory(selectGroupRosters, initMultiMapFactory(rosterInitByGroup, listen), { ...options, comparator: compareArray });
export const realizeMgrRostersForGroups = async (context, ids) => (await Promise.all(ids.map(id => realizeMgrRostersForGroup(context, id)))).flat();
export const [useMgrMemberRostersInRange] = dataHookWithCredentialsFactory(selectMemberRostersInRange, queryMapFactory(fetchMemberRostersInRange, 'rosterid', rosterInitByID, listen), { ...options, comparator: compareArray });
export const [useMgrUnconfirmedRostersInRange] = dataHookWithCredentialsFactory(selectUnconfirmedRosters, queryMapFactory(fetchUnconfirmedRostersInRange, 'rosterid', rosterInitByID, listen), { ...options, comparator: compareArray });
export const [useMgrRostersInRange] = dataHookWithCredentialsFactory(selectRostersInRange, queryMapFactory(fetchRostersInRange, 'rosterid', rosterInitByID, listen), { ...options, compare: compareArray });
export const [useMgrRosterRemovalsInRange] = dataHookWithCredentialsFactory(selectRosterRemovalsRequests, queryMapFactory(fetchRosterRemovalRequests, 'rosterid', rosterInitByID, listen), { ...options, compare: compareArray });
export const [useMgrMemberLastRoster, realizeMgrMemberLastRoster] = dataHookWithCredentialsFactory(selectLastRoster, queryMapFactory(fetchMemberLastRoster, 'rosterid', rosterInitByID, listen), options);

export const [useRosterRoles, realizeRosterRoles] = dataHookFactory(selectRosterRoles, { run: fetchRosterRoles, immortal: true });
export const useMgrRoster = internalid => {
    const roster = useSelector(selectRosterByInternalid(internalid));
    useMgrRosterByRosterID(roster?.rosterid);
    return roster;
}
export const useMgrRosters = internalids => {
    const rosters = useSelector(selectRostersByInternalid(internalids));
    const rosterids = useMemo(() => rosters.map(r => r.rosterid).filter(id => id), [rosters]);
    useMgrRostersByRosterID(rosterids);
    return rosters;
}

const reducer = { name: rosterMgrSlice.name, reducer: rosterMgrSlice.reducer }
export default reducer;